import React, {useMemo, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {Dropdown} from "primereact/dropdown";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import Icon from "../../../system/components/icon";
import InputLabel from "../../../system/components/input-label";
import {useTranslate} from "../../../system/useTranslate";

export const APP_URL = "https://app.zofino.ir/";
export const MAX_POPULAR_POSTS = 6;
const SUGGESTION_LIMIT = 6;

export function normalizeSearchText(value) {
    return String(value || "")
        .toLowerCase()
        .replace(/[يى]/g, "ی")
        .replace(/ك/g, "ک")
        .replace(/[ً-ٟ‌]/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

/**
 * Items matching the query: [{key, title, label, category, tags, ...}] sorted by title match quality.
 * Used for the suggestion dropdown and the search-results view.
 */
export function searchItems(items, query, limit = SUGGESTION_LIMIT) {
    const needle = normalizeSearchText(query);
    if (!needle) return [];
    const words = needle.split(" ").filter(Boolean);
    return (items || [])
        .map((item) => {
            const title = normalizeSearchText(item.title);
            const haystack = normalizeSearchText([item.title, item.summary, item.keywords, item.category, ...(item.tags || [])].join(" "));
            if (title.startsWith(needle)) return {item, rank: 0};
            if (title.includes(needle)) return {item, rank: 1};
            if (words.every((word) => haystack.includes(word))) return {item, rank: 2};
            return null;
        })
        .filter(Boolean)
        .sort((a, b) => a.rank - b.rank)
        .slice(0, limit)
        .map((entry) => entry.item);
}

/**
 * Search box with an auto-suggest dropdown. Picking a suggestion calls `onOpen(item)`;
 * Enter / the button without a highlighted suggestion calls `onSearch(query)`.
 */
export function HelpSearch({items = [], onOpen, onSearch, initialQuery = "", className = "w-8", placeholder}) {
    const translate = useTranslate();
    const [query, setQuery] = useState(initialQuery);
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(-1);
    const blurTimer = useRef();
    const suggestions = useMemo(() => searchItems(items, query), [items, query]);

    const pick = (item) => {
        setOpen(false);
        onOpen?.(item);
    };
    const submit = () => {
        if (active >= 0 && suggestions[active]) pick(suggestions[active]);
        else {
            setOpen(false);
            onSearch?.(query.trim());
        }
    };

    return (
        <div className={`relative ${className}`}>
            <div className="flex align-items-center justify-content-between bg-white p-2 shadow-2 border-round">
                <Icon icon="pi pi-search" className="mr-2 text-400"/>
                <InputText
                    className="flex-1 shadow-none border-none bg-transparent"
                    placeholder={placeholder || translate("help-search-placeholder")}
                    value={query}
                    role="combobox"
                    aria-expanded={open && Boolean(query.trim())}
                    aria-autocomplete="list"
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setActive(-1);
                        setOpen(true);
                    }}
                    onFocus={() => {
                        clearTimeout(blurTimer.current);
                        setOpen(true);
                    }}
                    onBlur={() => {
                        blurTimer.current = setTimeout(() => setOpen(false), 150);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "ArrowDown") {
                            e.preventDefault();
                            setActive((i) => Math.min(i + 1, suggestions.length - 1));
                        } else if (e.key === "ArrowUp") {
                            e.preventDefault();
                            setActive((i) => Math.max(i - 1, -1));
                        } else if (e.key === "Enter") {
                            e.preventDefault();
                            submit();
                        } else if (e.key === "Escape") {
                            setOpen(false);
                        }
                    }}
                />
                <Button label={translate("search")} onClick={submit}/>
            </div>
            {open && query.trim() && (
                <div className="absolute w-full bg-white border-1 border-300 border-round shadow-3 mt-1 z-5 text-right" role="listbox">
                    {suggestions.length === 0 ? (
                        <div className="p-3 text-xs text-500">{translate("help.search.no-results")}</div>
                    ) : suggestions.map((item, index) => (
                        <div
                            key={item.key}
                            role="option"
                            aria-selected={index === active}
                            className={`flex align-items-center gap-2 p-2 px-3 cursor-pointer text-xs ${index === active ? "surface-100" : "hover:surface-50"}`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => pick(item)}
                        >
                            <Icon icon={`mi mi:${item.icon || "article"}`} className="text-lg text-400"/>
                            <span className="flex flex-column">
                                <span className="font-bold">{item.title}</span>
                                <span className="text-400">{[item.label, item.category].filter(Boolean).join(" · ")}</span>
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

const EMPTY_REQUEST = {name: "", mobile: "", subject: "", description: "", category: null, priority: "normal"};

export function validateSupportRequest(values) {
    const errors = {};
    if (!values.name.trim()) errors.name = "help.request.required";
    if (!/^0?9\d{9}$/.test(String(values.mobile).replace(/[\s\-]/g, "").replace(/^\+98/, "0"))) errors.mobile = "help.request.mobile-invalid";
    if (!values.subject.trim()) errors.subject = "help.request.required";
    if (!values.description.trim()) errors.description = "help.request.required";
    return errors;
}

/**
 * Support request form (same fields as the new design). `onSubmit(values)` receives the validated values.
 * TODO(BACKEND): the admin-panel endpoint that stores the request does not exist yet.
 */
export function SupportRequestDialog({visible, onHide, onSubmit, categories = []}) {
    const translate = useTranslate();
    const [values, setValues] = useState(EMPTY_REQUEST);
    const [errors, setErrors] = useState({});
    const [sent, setSent] = useState(false);
    const set = (field) => (value) => setValues((current) => ({...current, [field]: value}));
    const close = () => {
        setValues(EMPTY_REQUEST);
        setErrors({});
        setSent(false);
        onHide?.();
    };
    const submit = async () => {
        const next = validateSupportRequest(values);
        setErrors(next);
        if (Object.keys(next).length) return;
        try {
            await onSubmit?.(values);
            setSent(true);
        } catch (error) {
            setErrors({submit: error?.response?.status === 429 ? "help.request.rate-limited" : "help.request.failed"});
        }
    };
    const priorities = ["normal", "blocked", "urgent"].map((value) => ({value, label: translate(`help.request.priority.${value}`)}));

    return (
        <Dialog
            visible={visible}
            onHide={close}
            header={translate("help.request.title")}
            className="rtl w-11 md:w-6 lg:w-4"
            pt={{root: () => ({className: "border-round-2xl"}), footer: () => ({className: "border-round-bottom-2xl"})}}
            footer={sent ? (
                <Button className="text-xs" label={translate("help.request.close")} onClick={close}/>
            ) : (
                <div className="flex flex-row-reverse gap-2">
                    <Button className="text-xs" label={translate("help.request.submit")} onClick={submit}/>
                    <Button className="p-button-text text-xs" label={translate("cancel")} onClick={close}/>
                </div>
            )}
        >
            {sent ? (
                <div className="flex flex-column align-items-center gap-2 p-3 text-center">
                    <Icon icon="mi mi:check_circle" className="text-4xl text-green-500"/>
                    <span className="font-bold">{translate("help.request.sent")}</span>
                    <span className="text-xs text-400">{translate("help.request.sent-description")}</span>
                </div>
            ) : (
                <div className="flex flex-column gap-3">
                    <InputLabel require label={translate("help.request.name")} inputType="TEXT" value={values.name} onChange={(e) => set("name")(e.target.value)}/>
                    {errors.name && <small className="p-error text-2xs">{translate(errors.name)}</small>}
                    <InputLabel require label={translate("help.request.mobile")} inputType="TEXT" value={values.mobile} onChange={(e) => set("mobile")(e.target.value)}/>
                    {errors.mobile && <small className="p-error text-2xs">{translate(errors.mobile)}</small>}
                    <InputLabel require label={translate("help.request.subject")} inputType="TEXT" value={values.subject} onChange={(e) => set("subject")(e.target.value)}/>
                    {errors.subject && <small className="p-error text-2xs">{translate(errors.subject)}</small>}
                    <InputLabel require label={translate("help.request.description")}>
                        <InputTextarea rows={4} className="w-full text-xs" value={values.description} onChange={(e) => set("description")(e.target.value)}/>
                    </InputLabel>
                    {errors.description && <small className="p-error text-2xs">{translate(errors.description)}</small>}
                    {errors.submit && <small className="p-error text-2xs">{translate(errors.submit)}</small>}
                    <InputLabel label={translate("help.request.category")}>
                        <Dropdown
                            className="w-full text-xs"
                            panelClassName="rtl text-xs"
                            showClear
                            value={values.category}
                            options={categories.map((item) => ({value: item.documentId, label: item.title}))}
                            placeholder={translate("help.request.select")}
                            onChange={(e) => set("category")(e.value)}
                        />
                    </InputLabel>
                    <InputLabel label={translate("help.request.priority.label")}>
                        <Dropdown className="w-full text-xs" panelClassName="rtl text-xs" value={values.priority} options={priorities} onChange={(e) => set("priority")(e.value)}/>
                    </InputLabel>
                </div>
            )}
        </Dialog>
    );
}
